// NOTE: please keep this JSON config sorted alphabetically to keep consistency between region config files

import {
  PaymentMethod,
  SUPPORTED_COUNTRY,
  SUPPORTED_LANGUAGE,
  MaxAgeExpiration,
} from 'utils/constants';
import { OrderSearchOperationName } from 'ui/forms/OrderLookUp/types';
import { commonConfig } from 'sites/common/config';

import { SiteConfig } from '../types';

import { BGFieldProps } from './fields';
import { BGForms } from './forms';
import { bgStates } from './states';

export const config: SiteConfig = {
  ...commonConfig,
  addressCountries: [{ label: 'Bulgaria', value: SUPPORTED_COUNTRY.BG }],
  addressFormat: [
    ['firstName', 'lastName'],
    ['address1', 'address2'],
    ['city'],
    ['postalCode'],
    ['countryCode'],
  ],
  addressFormatOneLiner:
    '@{firstName} @{lastName}, @{address1} @{address2} @{city}, @{postalCode} @{countryCode}',
  addressSearch: {
    billing: ['BG'],
    shipping: ['BG'],
  },
  addressStates: { BG: bgStates },
  analytics: { releaseVersion: `Bulgaria | ${process.env.VERSION}` },
  applePaySupportedNetworks: ['visa', 'masterCard', 'maestro', 'discover'],
  badgeIdsExcludedBySaleStatus: [],
  bloomreach: {
    objectName: 'br_data',
    viewId: 'bg',
  },
  charactersNeededToSubmitSearch: 1,
  cookieConsent: {
    documentLanguage: true,
    provider: 'OneTrust',
  },
  countryCode: 'bg',
  countryName: 'Bulgaria',
  currency: {
    code: 'EUR',
    decimalsToDisplay: 2,
    symbol: '€',
  },
  defaultPaymentMethod: PaymentMethod.SCHEME,
  fieldProps: { ...BGFieldProps },
  flag: '🇧🇬',
  forms: { ...BGForms },
  maxAgeCookiesExpires: MaxAgeExpiration.DAILY,
  maxCountRecordKeeper: 0,
  myAccountDashboardConfiguration: [
    {
      childOrder: [
        'profileComponent',
        'passwordComponent',
        'preferencesComponent',
        'ambassadorComponent',
      ],
      name: 'leftColumn',
    },
    {
      childOrder: ['addressBookComponent', 'orderHistoryComponent'],
      name: 'rightColumn',
    },
  ],
  newsletter: {
    contactSources: {
      checkOut: '5',
      footer: '1',
      signUp: '6',
    },
    fields: {
      gender: {
        required: true,
        values: ['male', 'female', 'noAnswer'],
      },
    },
  },
  orderLookUpQuery: OrderSearchOperationName['SearchOrderByEmail'],
  orderedPaymentPartners: [
    'visa',
    'master',
    'maestro',
    'payPal',
    'applePay',
    'klarna',
  ],
  paymentMethodsMixedWithGiftCard: [
    PaymentMethod.SCHEME,
    PaymentMethod.ADYEN_PAYPAL,
    PaymentMethod.KLARNA,
    PaymentMethod.KLARNA_ACCOUNT,
  ],
  pdpVideosConfig: {
    ...commonConfig.pdpVideosConfig,
    numberOfLoops: 2,
    posterSecond: 4,
  },
  pixleeId: {
    en: '19802791',
  },
  priceInputMaxLength: 4,
  priceSliderInterval: 5,
  pumaAppDownloadUrl: 'https://app.puma.com/web-download-uk',
  region: 'europe',
  regionCode: 'BG',
  savedCreditCardTemplate: ['new', 'saved'],
  searchEngineIndexableLanguages: [SUPPORTED_LANGUAGE.EN],
  searchSymbolsMinValue: 2,
  selectablePaymentMethods: [
    PaymentMethod.SCHEME,
    PaymentMethod.ADYEN_PAYPAL,
    PaymentMethod.AMBASSADOR_CARD,
    PaymentMethod.KLARNA,
    PaymentMethod.KLARNA_ACCOUNT,
  ],
  socialMediaLinks: {
    instagram: 'https://instagram.com/puma.uk?igshid=YzAyZWRlMzg=',
  },
  staticFeatures: {
    ...commonConfig.staticFeatures,
    addAddressResultErrorDisabled: true,
    allowGuestAccessToOrderDetails: true,
    ambassadorCardsEnabled: true,
    canRemoveWishlistItemsFromPDP: true,
    delayForterLoad: true,
    displayPromoDiscountBreakdown: false,
    editPasswordResultErrorDisabled: true,
    enableModelMeasurements: true,
    hideEmailNotifications: true,
    hidePickUpWhereYouLetOff: true,
    multipleModelMeasurementEnabled: true,
    parcelLabTrackingEnabled: true,
    popoverLoginEnabled: true,
    productFilterCollapsible: false,
    quickAddressFinder: false,
    removeReturnIframe: true,
    returnsOnOrderDetailsPage: true,
    showAddAddressButtonInAccountDashboard: false,
    showCookieBanner: false,
    showCookieConsentLayer: true,
    showFinancialIncentivesLink: false,
    showFreeShippingCost: true,
    showHeaderSubHeaderProductDetail: false,
    showModifiedResetPassFormPopup: false,
    showNewsletterSignupOnOrderConfirmation: false,
    showOrderSummaryAtOrderHistory: true,
    showOrderTrackingNumberInOrderDetails: true,
    showPaymentPartnersInFooter: true,
    showPhoneInCheckout: false,
    showPinterestFooterIcon: true,
    showPriceDifference: true,
    showPromoExclusionMsgInBaseSize: true,
    showTimeZoneName: true,
    singInInConfirmationForm: true,
    personalizationEnabled: true,
    ukAddressForm: true,
    useNewPaymentForm: true,
    useVerticalMedia: true,
    useDataguard: true,
  },
  supportedLocales: ['en-bg'],
  validation: {
    address: {
      length: commonConfig.validation.address.length,
      line1: "^[a-zA-Z0-9ÁáÉéÍíÓóÚúÜü\\-/ ' ]*$",
      line2: '^[a-zA-Z0-9-/ ]*$',
    },
    companyName: {
      length: {
        max: 50,
        min: 0,
      },
    },
    email: '^[\\w.%+-]+@[\\w.-]+\\.[\\w]{2,6}$',
    name: {
      length: commonConfig.validation.name.length,
    },
    password:
      '(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[-!@#$%^&*.+(){}~?])(?=.{12,})',
    phone: {
      length: {
        max: 30,
        min: commonConfig.validation.phone.length.min,
      },
      regex: '^.*$',
    },
    postalCode: {
      BG: '^(GIR[ ]?0AA)|((([ABCDEFGHIJKLMNOPRSTUWYZ][0-9][0-9]?)|(([ABCDEFGHIJKLMNOPRSTUWYZ][ABCDEFGHKLMNOPQRSTUVWXY][0-9][0-9]?)|(([ABCDEFGHIJKLMNOPRSTUWYZ][0-9][ABCDEFGHJKSTUW])|([ABCDEFGHIJKLMNOPRSTUWYZ][ABCDEFGHKLMNOPQRSTUVWXY][0-9][ABEHMNPRVWXY]))))[ ]?[0-9][ABDEFGHJLNPQRSTUWXYZ]{2})$',
    },
  },
};
